import { Injectable } from '@angular/core';
import { SyllabusQuery } from '@examdojo/category';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { isAngularHttpError } from '@examdojo/models/error-handler';
import { QuestionEntityService } from '@examdojo/question';
import { ReferenceQuestionService } from '@examdojo/reference-question';
import {
  DerivativeQuestionReviewType,
  getDerivativeModeFromStatus,
  getReferenceModeFromStatus,
  QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  ReferenceQuestionReviewType,
  ReviewItemQuery,
  ReviewItemService,
  ReviewItemStoreModel,
  ReviewPermissionsQuery,
  ReviewSolutionImageService,
} from '@examdojo/review';
import { UserQuery } from '@examdojo/user';
import { assertNonNullable } from '@examdojo/util/assert';
import { BehaviorSubject, EMPTY, finalize, first, of, switchMap, tap } from 'rxjs';

@Injectable()
export class QuestionValidationService {
  constructor(
    private readonly reviewItemService: ReviewItemService,
    private readonly reviewItemQuery: ReviewItemQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly questionService: QuestionEntityService,
    private readonly referenceQuestionService: ReferenceQuestionService,
    private readonly reviewSolutionImageService: ReviewSolutionImageService,
    private readonly userQuery: UserQuery,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly reviewPermissionsQuery: ReviewPermissionsQuery,
  ) {}

  readonly creatingReviewItem$$ = new BehaviorSubject<boolean>(false);

  readonly activeReviewItem$ = this.reviewItemQuery.active$;

  readonly creatingReviewItemError$$ = new BehaviorSubject<string | null>(null);
  readonly permissionError$$ = new BehaviorSubject<{ error_code: string; message: string } | null>(null);

  skipReviewItem() {
    const userId = this.userQuery.getActiveId();
    assertNonNullable(userId, 'userId');

    const activeReviewItem = this.reviewItemQuery.getActive();
    assertNonNullable(activeReviewItem, 'activeReviewItem');

    return this.reviewItemService
      .skipReviewItem(userId, activeReviewItem.id)
      .pipe(this.errorHandlerService.catchHttpErrors(() => of(null), { showErrorDetailsInToast: true }));
  }

  refreshReviewItem(reviewConfig: ReviewConfig) {
    return this.reviewItemService.fetchActive().pipe(
      switchMap(() => this.resetReviewItem(reviewConfig)),
      this.errorHandlerService.catchHttpErrors(() => of(null), { showErrorDetailsInToast: true }),
    );
  }

  resetReviewItem(reviewConfig: ReviewConfig) {
    const activeReviewItem = this.reviewItemQuery.getActive();
    const currentSyllabus = this.syllabusQuery.getActive();

    if (activeReviewItem && reviewConfig && currentSyllabus) {
      return this.shouldSkipReviewItemCreation(reviewConfig, currentSyllabus.id, activeReviewItem).pipe(
        switchMap((shouldSkip) => {
          if (shouldSkip) {
            console.debug('Skipping review item creation - mode and syllabus match active review item');
            return of(null);
          }

          const obs$ = activeReviewItem ? this.reviewItemService.cancelQuestion(activeReviewItem.id) : of(null);
          this.creatingReviewItem$$.next(true);

          return obs$.pipe(
            switchMap(() =>
              this.createNewReviewItem(reviewConfig).pipe(
                this.errorHandlerService.catchHttpErrors(() => of(null), { showErrorDetailsInToast: true }),
              ),
            ),
            finalize(() => this.creatingReviewItem$$.next(false)),
          );
        }),
      );
    }

    const obs$ = activeReviewItem ? this.reviewItemService.cancelQuestion(activeReviewItem.id) : of(null);

    this.creatingReviewItem$$.next(true);

    return obs$.pipe(
      switchMap(() =>
        this.createNewReviewItem(reviewConfig).pipe(
          this.errorHandlerService.catchHttpErrors(() => of(null), { showErrorDetailsInToast: true }),
        ),
      ),
      finalize(() => this.creatingReviewItem$$.next(false)),
    );
  }

  private createNewReviewItem(reviewConfig: ReviewConfig) {
    this.creatingReviewItemError$$.next(null);
    this.creatingReviewItem$$.next(true);
    this.questionService.resetStore();

    return this.getNextReviewItem(reviewConfig).pipe(
      tap(() => {
        this.reviewSolutionImageService.reset();
      }),
      tap((response) => {
        if (response === USER_HAS_NO_PERMISSION_ERROR) {
          this.creatingReviewItemError$$.next(response.error_code);
        }
      }),
      this.errorHandlerService.catchHttpErrors(
        (err) => {
          this.creatingReviewItemError$$.next(
            isAngularHttpError(err) ? err.error.error_code : err instanceof Error ? err.message : 'Unknown error',
          );
          return EMPTY;
        },
        {
          showErrorDetailsInToast: true,
          ignoreReporting: (err) => {
            return isAngularHttpError(err) && err.status === 404;
          },
        },
      ),
      tap(() => this.creatingReviewItem$$.next(false)),
      finalize(() => this.creatingReviewItem$$.next(false)),
    );
  }

  private shouldSkipReviewItemCreation(
    config: ReviewConfig,
    currentSyllabusId: number,
    activeReviewItem: ReviewItemStoreModel,
  ) {
    // check if the mode matches the mode determined by the question's status
    if (config.type === 'derivative' && activeReviewItem.question_id) {
      return this.questionService.fetchQuestionStatus(activeReviewItem.question_id).pipe(
        switchMap((status) => {
          const statusBasedMode = getDerivativeModeFromStatus(status as string | null);
          if (statusBasedMode !== config.mode) {
            return of(false);
          }

          // check if the syllabus matches
          return this.questionService
            .fetchSyllabusIdByQuestionId(activeReviewItem.question_id!)
            .pipe(switchMap((syllabusId) => of(syllabusId === currentSyllabusId)));
        }),
      );
    }

    if (config.type === 'reference' && activeReviewItem.reference_question_id) {
      return this.referenceQuestionService.fetchReferenceQuestionStatus(activeReviewItem.reference_question_id).pipe(
        switchMap((status) => {
          const statusBasedMode = getReferenceModeFromStatus(status as string | null);
          if (statusBasedMode !== config.mode) {
            return of(false);
          }

          // check if the syllabus matches
          return this.referenceQuestionService
            .fetchSyllabusIdByReferenceQuestionId(activeReviewItem.reference_question_id!)
            .pipe(switchMap((syllabusId) => of(syllabusId === currentSyllabusId)));
        }),
      );
    }

    console.error('No review item type found in the active review item');

    return of(false);
  }

  private getNextReviewItem(config: ReviewConfig) {
    return this.syllabusQuery.active$.pipe(
      first(isNotNullish),
      switchMap((syllabus) => {
        const permissionRole =
          config.type === 'reference'
            ? REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[config.mode]
            : QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[config.mode];

        const hasPermission = this.reviewPermissionsQuery.hasReviewPermission(syllabus.id, permissionRole);
        if (!hasPermission) {
          return of(USER_HAS_NO_PERMISSION_ERROR);
        }

        if (config.type === 'reference') {
          return this.reviewItemService.getNextReferenceQuestionReviewItem(config.mode, syllabus.id);
        } else {
          return this.reviewItemService.getNextQuestionReviewItem(config.mode, syllabus.id);
        }
      }),
    );
  }
}

export enum CreatingReviewItemBackendError {
  NoCandidateQuestions = 'no_candidate_questions_error',
  UserHasNoPermission = 'review_permission_denied_error',
}

export const USER_HAS_NO_PERMISSION_ERROR = {
  // Mimic the error code from the backend
  error_code: CreatingReviewItemBackendError.UserHasNoPermission,
  message: 'You do not have permission to review this item',
};

export type ReviewConfig =
  | {
      type: 'derivative';
      mode: DerivativeQuestionReviewType;
    }
  | {
      type: 'reference';
      mode: ReferenceQuestionReviewType;
    };
